import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { birthdayToDate, getBirthdayError } from '../components/auth/loginValidation';
import { buildOAuthStartUrl, navigateToOAuthStart } from '../lib/auth/startOAuth';
import type { LoginTab } from '../components/auth/LoginTabs';

export const AGE_REQUIRED_MESSAGE =
  'To create an account with Google, enter your date of birth and country first.';

/** Create Account asks for the age before any provider, as the mobile app does. */
export const AGE_FIRST_MESSAGE = 'Please enter your date of birth and country first.';

export const AGE_INVALID_MESSAGE =
  'We could not verify your age. Check your date of birth and country, then try again.';

interface UseOAuthAgeDeclarationArgs {
  activeTab: LoginTab;
  birthday: string;
  country: string;
  onBirthdayError: (message: string) => void;
  onFormError: (message: string) => void;
}

/**
 * Web OAuth age check (#353). Existing users are never asked for a date of
 * birth: a declaration is only posted when both fields are filled. A brand new
 * identity is refused server-side and returns as `?error=age_required`, which
 * reveals the fields on the sign-in tab (`showOAuthAge`).
 */
export const useOAuthAgeDeclaration = ({
  activeTab,
  birthday,
  country,
  onBirthdayError,
  onFormError,
}: UseOAuthAgeDeclarationArgs) => {
  const [showOAuthAge, setShowOAuthAge] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Friendly messages for a failed auth redirect (`?error=...`), then clean the URL.
  useEffect(() => {
    const error = searchParams.get('error');
    if (error === 'auth_failed') {
      onFormError('Something went wrong during sign in. Please try again.');
    } else if (error === 'age_required') {
      setShowOAuthAge(true);
      onFormError(AGE_REQUIRED_MESSAGE);
    } else if (error === 'age_invalid') {
      setShowOAuthAge(true);
      onFormError(AGE_INVALID_MESSAGE);
    } else {
      return;
    }
    searchParams.delete('error');
    setSearchParams(searchParams, { replace: true });
    // onFormError is a state setter; the redirect is handled once per URL change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, setSearchParams]);

  // When the age fields are not on screen an error pinned to them would be
  // invisible and the button would look dead, so surface it at form level.
  const fieldsVisible = activeTab === 'signup' || showOAuthAge;
  const reportError = (message: string) =>
    fieldsVisible ? onBirthdayError(message) : onFormError(message);

  // Navigates to the API, which checks the age again server-side and starts OAuth (#393).
  const handleOAuthSignin = (provider: string) => {
    const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';
    const declaring = Boolean(birthday && country);
    // On Create Account the fields sit right above the button, so a missing age
    // is reported there rather than discovered after a round trip to Google.
    if (!declaring && activeTab === 'signup') {
      reportError(AGE_FIRST_MESSAGE);
      return;
    }
    if (declaring) {
      const ageError = getBirthdayError(birthday, country);
      if (ageError) {
        reportError(ageError);
        return;
      }
    }
    navigateToOAuthStart(
      buildOAuthStartUrl({
        apiBase,
        provider,
        ...(declaring && {
          birthday: birthdayToDate(birthday).toISOString(),
          country,
        }),
      })
    );
  };

  return { showOAuthAge, setShowOAuthAge, handleOAuthSignin };
};
